import settings from 'popups/Settings'
import { format12Hours, format24Hours } from 'pages/calendar/components/EventEditor/timeFormats'

export interface ReminderAlarm {
  action: string
  trigger:
    | {
        params: Record<string, string>
        val: string
      }
    | string
    | Date
}

export interface ReminderOption {
  key: string
  label: string
  value: Date | string | null
  isDateTime: boolean
}

export interface ReminderSelectorRef {
  notifyUserDueDateChange: () => void
}

interface TodoSettings {
  defaultReminderStartTime?: number
  defaultReminderTimeForToday?: number
  defaultReminderTimeForFuture?: string
}

/**
 * Today 다음 시간대 계산 (Android spec 을 가져옴)
 *  defaultReminderTimeForToday
 *  - 0 (Auto)
 *     3시가 넘은 경우 +3시간, 그 외 16시
 *  - 10, 30, 60, 120 (분)
 *     설정 값만큼 더함
 */
export const getTodayReminderTime = (defaultReminderTimeForToday: number): Date => {
  const now = new Date()
  const nextHour = new Date(now)

  if (defaultReminderTimeForToday === 0) {
    // auto
    if (now.getHours() >= 15) {
      nextHour.setHours(now.getHours() + 3, 0, 0, 0)
    } else {
      nextHour.setHours(16, 0, 0, 0)
    }
  } else {
    nextHour.setMinutes(now.getMinutes() + defaultReminderTimeForToday, 0, 0)
  }

  return nextHour
}

// working hour 시간 계산 함수
export const getReminderTimeByDate = (date: Date, workingHourMinutes: number): Date => {
  const workingHours = Math.floor(workingHourMinutes / 60)
  const workingMinutes = workingHourMinutes % 60
  const result = new Date(date)
  result.setHours(workingHours, workingMinutes, 0, 0)
  return result
}

/**
 * Calculate reminder date-time based on due date and settings
 */
export const calculateReminderDateTime = (
  dueDate: Date,
  defaultReminderTimeForFuture: string,
  defaultReminderTime: number,
  defaultReminderTimeForToday: number,
): Date | null => {
  if (!dueDate) return null

  const reminderDate = new Date(dueDate)

  // Check if dueDate is today
  const today = new Date()
  const isToday =
    dueDate.getFullYear() === today.getFullYear() &&
    dueDate.getMonth() === today.getMonth() &&
    dueDate.getDate() === today.getDate()

  // Convert minutes to hours and minutes
  const hours = Math.floor(defaultReminderTime / 60)
  const minutes = defaultReminderTime % 60

  switch (defaultReminderTimeForFuture) {
    case 'dayBefore': // Day before due at 9:00 AM
      reminderDate.setDate(dueDate.getDate() - 1)
      reminderDate.setHours(hours, minutes, 0, 0)
      break
    case 'weekBefore': // 1 week before due at 9:00 AM
      reminderDate.setDate(dueDate.getDate() - 7)
      reminderDate.setHours(hours, minutes, 0, 0)
      break
    case 'dueDate': // Day of due at 9:00 AM
    default:
      if (isToday) {
        const nextHour = getTodayReminderTime(defaultReminderTimeForToday)
        return nextHour
      } else {
        reminderDate.setHours(hours, minutes, 0, 0)
      }
      break
  }

  return reminderDate
}

// 새로운 reminder 옵션 생성 함수 (QuickAddTaskEditor에서 사용)
export const createReminderOptions = (
  t: (key: string) => string,
  hasExistingReminder: boolean = false,
  removePickDate: boolean = false,
): ReminderOption[] => {
  const todoSettings = (settings.getValue('todo') as TodoSettings) || {}
  const defaultReminderStartTime = todoSettings.defaultReminderStartTime || 540 // 9AM
  const defaultReminderTimeForToday = todoSettings.defaultReminderTimeForToday || 0

  // Get time format setting
  const settingTimeFormat = settings.getValue('general').timeFormat as string

  const is12HourFormat = settingTimeFormat === 'h:mm A'
  const formatTime = is12HourFormat ? format12Hours : format24Hours

  const today = new Date()
  const tomorrow = new Date(today)
  tomorrow.setDate(today.getDate() + 1)

  // 현재 시간에서 다음 시간대 계산
  const nextHour = getTodayReminderTime(defaultReminderTimeForToday)

  // working hour 시간 계산
  const tomorrowReminderTime = getReminderTimeByDate(tomorrow, defaultReminderStartTime)

  // 다음 주 월요일 계산
  const nextWeekMonday = new Date(today)
  const daysUntilMonday = (8 - today.getDay()) % 7 || 7
  nextWeekMonday.setDate(today.getDate() + daysUntilMonday)
  const nextWeekRedminderTime = getReminderTimeByDate(nextWeekMonday, defaultReminderStartTime)

  const options: ReminderOption[] = [
    {
      key: 'today',
      label: `${t('todo.editor.today')}, ${formatTime(nextHour, null)}`,
      value: nextHour,
      isDateTime: true,
    },
    {
      key: 'tomorrow',
      label: `${t('todo.editor.tomorrow')}, ${formatTime(tomorrowReminderTime, null)}`,
      value: tomorrowReminderTime,
      isDateTime: true,
    },
    {
      key: 'next_week',
      label: `${t('todo.editor.nextWeek')}, ${formatTime(nextWeekRedminderTime, null)}`,
      value: nextWeekRedminderTime,
      isDateTime: true,
    },
  ]

  if (!removePickDate) {
    options.push({
      key: 'pick_date',
      label: t('todo.editor.pickDateAndTime'),
      value: 'pick_date',
      isDateTime: false,
    })
  }

  // Only add "off" option if there's an existing reminder
  if (hasExistingReminder) {
    options.unshift({
      key: 'off',
      label: t('todo.editor.off'),
      value: null,
      isDateTime: false,
    })
  }

  return options
}
